import React, { useEffect, useRef, useState } from 'react';
import { Search, Loader2, School as SchoolIcon, MapPin, Globe2, Network, X } from 'lucide-react';
import api from '../api/client';

/**
 * Universal search bar for the landing page. Types-as-you-search across schools,
 * districts, states and chains. Selecting a result invokes onSelect(kind, item).
 */
const UniversalSearchBar = ({ onSelect }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const abortRef = useRef(null);

  // Debounced fetch
  useEffect(() => {
    if (!q || q.trim().length < 2) {
      setResults(null); setLoading(false);
      return;
    }
    setLoading(true);
    const controller = new AbortController();
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = controller;
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/search?q=${encodeURIComponent(q.trim())}&limit=10`, { signal: controller.signal });
        setResults(res.data);
      } catch (e) {
        if (e.name !== 'CanceledError' && e.name !== 'AbortError') console.error(e);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [q]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const flat = React.useMemo(() => {
    if (!results) return [];
    const arr = [];
    (results.schools || []).forEach((s) => arr.push({ kind: 'school', item: s }));
    (results.chains || []).forEach((c) => arr.push({ kind: 'chain', item: c }));
    (results.districts || []).forEach((d) => arr.push({ kind: 'district', item: d }));
    (results.states || []).forEach((s) => arr.push({ kind: 'state', item: s }));
    return arr;
  }, [results]);

  const handleKey = (e) => {
    if (!open || flat.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(h + 1, flat.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = flat[highlight];
      if (hit) { onSelect?.(hit.kind, hit.item); setOpen(false); }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative w-full max-w-2xl mx-auto" data-testid="universal-search">
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setHighlight(0); }}
          onFocus={() => q && setOpen(true)}
          onKeyDown={handleKey}
          data-testid="universal-search-input"
          placeholder="Search your school, district, UDISE, or chain..."
          className="w-full pl-12 pr-12 py-4 rounded-2xl bg-white/95 backdrop-blur border-2 border-slate-200/80 shadow-lg shadow-indigo-100/50 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 text-base transition-all"
        />
        {loading && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-indigo-500 animate-spin" />}
        {!loading && q && (
          <button
            type="button"
            onClick={() => { setQ(''); setResults(null); setOpen(false); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded"
            data-testid="universal-search-clear"
          ><X className="w-4 h-4" /></button>
        )}
      </div>

      {open && results && (
        <div className="absolute top-full left-0 right-0 mt-2 rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden z-40 max-h-[70vh] overflow-y-auto" data-testid="universal-search-dropdown">
          {results.total === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">
              No results for <span className="font-semibold text-slate-700">"{q}"</span>. Try a district or UDISE.
            </div>
          ) : (
            <>
              {results.schools?.length > 0 && <SectionLabel label="Schools" />}
              {results.schools?.map((s, i) => (
                <ResultRow
                  key={`sch-${s.id}`}
                  icon={SchoolIcon}
                  gradient="from-indigo-500 to-violet-500"
                  title={s.school_name}
                  subtitle={`${s.city ? s.city + ' · ' : ''}${s.district_name || ''}${s.district_name ? ' · ' : ''}${s.state_name}${s.udise_number ? ' · ' + s.udise_number : ''}`}
                  active={highlight === flatIndex(flat, 'school', s)}
                  onClick={() => { onSelect?.('school', s); setOpen(false); }}
                  testId={`search-school-${s.id}`}
                />
              ))}
              {results.chains?.length > 0 && <SectionLabel label="School Chains" />}
              {results.chains?.map((c) => (
                <ResultRow
                  key={`chn-${c.id}`}
                  icon={Network}
                  gradient="from-fuchsia-500 to-pink-500"
                  title={c.group_name}
                  subtitle={c.is_national ? 'National chain — click to view branches' : 'State chain — click to view branches'}
                  active={highlight === flatIndex(flat, 'chain', c)}
                  onClick={() => { onSelect?.('chain', c); setOpen(false); }}
                  testId={`search-chain-${c.id}`}
                />
              ))}
              {results.districts?.length > 0 && <SectionLabel label="Districts" />}
              {results.districts?.map((d) => (
                <ResultRow
                  key={`dst-${d.id}`}
                  icon={MapPin}
                  gradient="from-emerald-500 to-teal-500"
                  title={d.district_name}
                  subtitle={d.state_name}
                  active={highlight === flatIndex(flat, 'district', d)}
                  onClick={() => { onSelect?.('district', d); setOpen(false); }}
                  testId={`search-district-${d.id}`}
                />
              ))}
              {results.states?.length > 0 && <SectionLabel label="States" />}
              {results.states?.map((s) => (
                <ResultRow
                  key={`st-${s.id}`}
                  icon={Globe2}
                  gradient="from-amber-500 to-orange-500"
                  title={s.state_name}
                  subtitle={s.state_code || 'India'}
                  active={highlight === flatIndex(flat, 'state', s)}
                  onClick={() => { onSelect?.('state', s); setOpen(false); }}
                  testId={`search-state-${s.id}`}
                />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const flatIndex = (flat, kind, item) => flat.findIndex((x) => x.kind === kind && x.item.id === item.id);

const SectionLabel = ({ label }) => (
  <div className="px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-slate-400 bg-slate-50 border-b border-slate-100 sticky top-0">{label}</div>
);

const ResultRow = ({ icon: Icon, gradient, title, subtitle, active, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`w-full text-left flex items-center gap-3 px-4 py-3 transition-colors border-l-4 ${active ? 'bg-indigo-50 border-indigo-500' : 'bg-white border-transparent hover:bg-slate-50'}`}
  >
    <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${gradient} flex items-center justify-center text-white flex-shrink-0 shadow-md`}>
      <Icon className="w-4 h-4" />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-semibold text-slate-900 truncate">{title}</p>
      <p className="text-xs text-slate-500 truncate">{subtitle}</p>
    </div>
  </button>
);

export default UniversalSearchBar;
